import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { baseUrl } from '../../utils/Const';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleInputFocus = () => {
    toast.dismiss();
  };

  const validateForm = () => {
    if (!formData.password) {
      toast.error("Please enter a new password");
      return false;
    }

    if (!formData.confirmPassword) {
      toast.error("Please confirm your password");
      return false;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    // Strong password validation
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordPattern.test(formData.password)) {
      toast.error("Password must contain uppercase, lowercase, number, and special character");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();

    if (!validateForm()) {
      return;
    }

    if (!token) {
      toast.error("Invalid or expired reset link");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${baseUrl}reset-password/${token}`, { 
        password: formData.password 
      });
      
      console.log("Password reset successful:", response);
      
      toast.success("Password has been reset successfully! Redirecting to login...", {
        position: "top-right",
        autoClose: 2000,
        onClose: () => navigate("/"),
      });

      setFormData({ password: '', confirmPassword: '' });

    } catch (error) {
      console.error("Error resetting password:", error);
      
      if (error.response?.status === 400) {
        toast.error("Invalid or expired reset token");
      } else if (error.response?.status === 404) {
        toast.error("Reset link has expired. Please request a new one.");
      } else if (error.response?.status === 401) {
        toast.error("Unauthorized access. Please try again.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-gradient-to-br from-[#FFF8F0] to-[#F8F3E9]">
      <ToastContainer />
      <div className="flex justify-center items-center h-screen">
        <div className="w-full max-w-md">
          
          <form
            onSubmit={handleSubmit}
            className="bg-white border-2 border-[#E6B17E] shadow-2xl rounded-2xl px-8 py-8 mx-4"
          >
            {/* Logo/Title Section */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#D35400] to-[#E67E22] rounded-full mb-4 shadow-lg">
                <span className="text-3xl text-white font-bold">CR</span>
              </div>
              <p className="text-3xl font-bold bg-gradient-to-r from-[#D35400] to-[#E67E22] bg-clip-text text-transparent">
                CloudRasoi
              </p>
              <p className="text-sm text-[#7E6B5A] mt-1">Restaurant Management System</p>
            </div>

            <h2 className="text-xl font-bold mb-2 text-center text-[#2C3E50] border-b border-[#E6B17E] pb-3">
              Create New Password
            </h2>
            
            <p className="text-[#7E6B5A] mb-6 text-center text-sm">
              Enter and confirm your new password below
            </p>

            {/* New Password */}
            <div className="mb-4">
              <label className="block text-[#5D4037] text-sm font-semibold mb-2">
                New Password <span className="text-[#D32F2F]">*</span>
              </label>
              <input
                type="password"
                name="password"
                placeholder="Enter new password"
                value={formData.password}
                onChange={handleChange}
                onFocus={handleInputFocus}
                className="w-full p-3 rounded-lg border-2 border-[#D7CCC8] bg-white text-[#5D4037] placeholder-[#A1887F] focus:outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/30 transition-all duration-300"
              />
              <div className="mt-2">
                <p className="text-xs text-[#7E6B5A] font-medium mb-1">Password Requirements:</p>
                <ul className="text-xs text-[#7E6B5A] ml-4 space-y-0.5">
                  <li className={`flex items-center ${formData.password.length >= 8 ? 'text-[#4CAF50]' : ''}`}>
                    <span className="mr-1">•</span>
                    At least 8 characters
                  </li>
                  <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-[#4CAF50]' : ''}`}>
                    <span className="mr-1">•</span>
                    One uppercase letter
                  </li>
                  <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-[#4CAF50]' : ''}`}>
                    <span className="mr-1">•</span>
                    One lowercase letter
                  </li>
                  <li className={`flex items-center ${/\d/.test(formData.password) ? 'text-[#4CAF50]' : ''}`}>
                    <span className="mr-1">•</span>
                    One number
                  </li>
                  <li className={`flex items-center ${/[@$!%*?&]/.test(formData.password) ? 'text-[#4CAF50]' : ''}`}>
                    <span className="mr-1">•</span>
                    One special character
                  </li>
                </ul>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="block text-[#5D4037] text-sm font-semibold mb-2">
                Confirm Password <span className="text-[#D32F2F]">*</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onFocus={handleInputFocus}
                className={`w-full p-3 rounded-lg border-2 ${formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-[#D32F2F]' : 'border-[#D7CCC8]'} bg-white text-[#5D4037] placeholder-[#A1887F] focus:outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/30 transition-all duration-300`}
              />
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-[#D32F2F] text-sm mt-1">Passwords do not match</p>
              )}
              {formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="text-[#4CAF50] text-sm mt-1">✓ Passwords match</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#D35400] to-[#E67E22] hover:from-[#E67E22] hover:to-[#D35400] text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#E67E22] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting Password...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

            {/* Back to Login */}
            <div className="mt-6 pt-4 border-t border-[#D7CCC8]">
              <div className="text-center">
                <p className="text-sm text-[#7E6B5A]">
                  <Link
                    to="/"
                    className="font-bold text-[#D35400] hover:text-[#E67E22] hover:underline underline-offset-2 transition-colors duration-300"
                  >
                    ← Back to Login
                  </Link>
                </p>
              </div>
            </div>

            {/* Security Note */}
            <div className="mt-4 p-3 bg-gradient-to-r from-[#FFF3E0] to-[#FFECB3] border-l-4 border-[#FF9800] rounded">
              <p className="text-[#EF6C00] text-xs">
                <span className="font-semibold">Security:</span> Choose a strong, unique password for your account.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;